package com.ijoin.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.runtime.snapshotFlow
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import androidx.lifecycle.viewmodel.compose.viewModel
import kotlinx.coroutines.flow.collect
import com.ijoin.getPlatform
import com.ijoin.i18n.strings
import com.ijoin.platform.isWebMobile
import com.ijoin.ui.components.EventCard
import com.ijoin.ui.components.RecommendedEventCard
import com.ijoin.ui.components.MainBottomNavigationBar
import com.ijoin.ui.components.SideNavigationRail
import com.ijoin.ui.components.TicketCard
import com.ijoin.ui.components.RefreshableContainer
import com.ijoin.ui.components.rememberPullToRefreshState
import com.ijoin.ui.components.isAtTop
import com.ijoin.ui.design.EventCardBackground
import com.ijoin.ui.design.EventFeedSectionHeader
import com.ijoin.ui.design.EventSearchField
import com.ijoin.ui.design.EventSectionHeader
import com.ijoin.ui.design.EventSectionTitle
import com.ijoin.ui.design.EventTopBar
import com.ijoin.ui.design.eventBackgroundBrush
import com.ijoin.ui.home.HomeViewModel
import com.ijoin.ui.test.TestTags

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun HomeScreen(
    onEventClick: (String) -> Unit,
    onTicketClick: (String) -> Unit,
    onCreateEventClick: () -> Unit,
    onOpenTickets: () -> Unit,
    onOpenProfile: () -> Unit,
    onOpenChats: () -> Unit,
    isAuthenticated: Boolean,
    modifier: Modifier = Modifier,
    viewModel: HomeViewModel = viewModel { HomeViewModel() }
) {
    val strings = strings()
    var searchQuery by remember { mutableStateOf("") }
    val uiState by viewModel.uiState.collectAsState()
    val platform = getPlatform()
    val isWeb = platform.isWeb
    val isMobileWeb = isWeb && isWebMobile()
    val isDesktopWeb = isWeb && !isWebMobile()
    val listState = rememberLazyListState()
    val rootModifier = modifier.testTag(TestTags.HomeScreen.ROOT)

    // Load data once when screen is first composed
    LaunchedEffect(isAuthenticated) {
        println("HomeScreen - LaunchedEffect(isAuthenticated) triggered")
        viewModel.loadData(forceRefresh = true, isAuthenticated = isAuthenticated)
    }

    LaunchedEffect(searchQuery) {
        if (searchQuery.isNotEmpty()) {
            viewModel.searchEvents(searchQuery)
        } else {
            viewModel.clearSearch()
        }
    }

    if (isDesktopWeb) {
        // Desktop web layout with side navigation
        Row(modifier = rootModifier.fillMaxSize()) {
            SideNavigationRail(
                selectedTab = "home",
                onTabSelected = { tab ->
                    when (tab) {
                        "create" -> onCreateEventClick()
                        "tickets" -> onOpenTickets()
                        "profile" -> onOpenProfile()
                        // Other tabs like home/explore can be handled if needed
                    }
                },
                showAppStoreLinks = true
            )

            // Center content with max width for web
            BoxWithConstraints(
                modifier = Modifier
                    .fillMaxSize()
                    .background(eventBackgroundBrush()),
                contentAlignment = Alignment.TopCenter
            ) {
                val contentWidth = if (maxWidth < 1200.dp) maxWidth else 1200.dp
                Box(
                    modifier = Modifier
                        .width(contentWidth)
                        .fillMaxHeight()
                ) {
                    HomeContent(
                        searchQuery = searchQuery,
                        onSearchQueryChange = { searchQuery = it },
                        uiState = uiState,
                        viewModel = viewModel,
                        onEventClick = onEventClick,
                        onTicketClick = onTicketClick,
                        strings = strings,
                        onOpenChats = onOpenChats,
                        modifier = Modifier.fillMaxSize(),
                        listState = listState
                    )
                }
            }
        }
    } else {
        // Mobile layout with bottom navigation
        Scaffold(
            modifier = rootModifier,
            bottomBar = {
                MainBottomNavigationBar(
                    selectedTab = "home",
                    onTabSelected = { tab ->
                        when (tab) {
                            "create" -> onCreateEventClick()
                            "tickets" -> onOpenTickets()
                            "profile" -> onOpenProfile()
                        }
                    }
                )
            },
            containerColor = EventCardBackground
        ) { paddingValues ->
            val pullState = rememberPullToRefreshState(
                refreshing = uiState.isLoading,
                onRefresh = { viewModel.loadData(forceRefresh = true, isAuthenticated = isAuthenticated) },
                canRefresh = { listState.isAtTop() }
            )
            RefreshableContainer(
                modifier = rootModifier.padding(paddingValues),
                state = pullState,
                enabled = true
            ) { pullModifier ->
                HomeContent(
                    searchQuery = searchQuery,
                    onSearchQueryChange = { searchQuery = it },
                    uiState = uiState,
                    viewModel = viewModel,
                    onEventClick = onEventClick,
                    onTicketClick = onTicketClick,
                    strings = strings,
                    onOpenChats = onOpenChats,
                    modifier = pullModifier,
                    listState = listState
                )
            }
        }
    }
}

@Composable
private fun HomeContent(
    searchQuery: String,
    onSearchQueryChange: (String) -> Unit,
    uiState: com.ijoin.ui.home.HomeUiState,
    viewModel: HomeViewModel,
    onEventClick: (String) -> Unit,
    onTicketClick: (String) -> Unit,
    strings: com.ijoin.i18n.Strings,
    onOpenChats: () -> Unit,
    modifier: Modifier = Modifier,
    listState: LazyListState
) {
    var showFavorites by rememberSaveable { mutableStateOf(false) }

    // Detect when user scrolls to the end to load more
    LaunchedEffect(listState, showFavorites) {
        snapshotFlow { listState.layoutInfo.visibleItemsInfo.lastOrNull()?.index }
            .collect { lastVisibleIndex ->
                val totalItems = listState.layoutInfo.totalItemsCount
                val shouldLoadMore = lastVisibleIndex != null &&
                    lastVisibleIndex >= totalItems - 3 &&
                    !uiState.isLoading &&
                    !uiState.isLoadingMore &&
                    uiState.feedItems.isNotEmpty() &&
                    !showFavorites
                if (shouldLoadMore) {
                    viewModel.loadMoreFeed()
                }
            }
    }

    LazyColumn(
        modifier = modifier
            .fillMaxSize()
            .background(eventBackgroundBrush()),
        state = listState
    ) {
        item {
            Spacer(modifier = Modifier.height(16.dp))
        }

        item {
            EventTopBar(
                title = strings.appName,
                actionIcon = Icons.Outlined.Send,
                actionContentDescription = strings.messagesTitle,
                onActionClick = {
                    viewModel.markChatsOpened()
                    onOpenChats()
                },
                showActionIndicator = uiState.hasUnreadMessages,
                modifier = Modifier.padding(horizontal = 16.dp),
                actionModifier = Modifier.testTag(TestTags.HomeScreen.CHAT_BUTTON)
            )
        }

        item {
            Spacer(modifier = Modifier.height(16.dp))
        }

        // Search bar
        item {
            EventSearchField(
                value = searchQuery,
                onValueChange = onSearchQueryChange,
                placeholder = strings.searchPlaceholder,
                leadingIcon = Icons.Outlined.Search,
                modifier = Modifier
                    .padding(horizontal = 16.dp)
                    .testTag(TestTags.HomeScreen.SEARCH_FIELD)
            )
        }

        item {
            Spacer(modifier = Modifier.height(24.dp))
        }

        // Loading indicator
        if (uiState.isLoading) {
            item {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(32.dp),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator()
                }
            }
        }

        // Content (when not loading)
        if (!uiState.isLoading) {
            // Search results (if searching)
            if (uiState.searchResults != null) {
                val results = uiState.searchResults!!
                if (results.isNotEmpty()) {
                    item {
                        EventSectionTitle(
                            title = strings.searchResults,
                            modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp)
                        )
                    }
                    item {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(horizontal = 16.dp),
                            verticalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            results.forEach { event ->
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .testTag("${TestTags.HomeScreen.SEARCH_RESULT_CARD_PREFIX}${event.id}")
                                ) {
                                    RecommendedEventCard(
                                        event = event,
                                        onEventClick = onEventClick,
                                        onJoinClick = onEventClick
                                    )
                                }
                            }
                        }
                    }
                    item {
                        Spacer(modifier = Modifier.height(24.dp))
                    }
                } else {
                    item {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(32.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                text = strings.noResults,
                                style = MaterialTheme.typography.bodyLarge,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
            }

            // My Tickets section
            if (uiState.upcomingTickets.isNotEmpty() && uiState.searchResults == null) {
                item {
                    EventSectionHeader(
                        title = strings.myTickets,
                        subtitle = strings.tapToViewTicket,
                        modifier = Modifier.padding(horizontal = 16.dp)
                    )
                }
                item {
                    Spacer(modifier = Modifier.height(12.dp))
                }
                item {
                    LazyRow(
                        contentPadding = PaddingValues(horizontal = 16.dp),
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                        modifier = Modifier.height(200.dp)
                    ) {
                        items(uiState.upcomingTickets) { ticket ->
                            TicketCard(
                                ticket = ticket,
                                onTicketClick = onTicketClick,
                                onFavoriteClick = { viewModel.toggleTicketFavorite(ticket.id) }
                            )
                        }
                    }
                }
                item {
                    Spacer(modifier = Modifier.height(32.dp))
                }
            }

            // Favorites section
            if (uiState.favoriteEvents.isNotEmpty() && uiState.searchResults == null && !showFavorites) {
                item {
                    EventSectionHeader(
                        title = strings.favoriteEvents,
                        subtitle = strings.eventsYouSaved,
                        modifier = Modifier.padding(horizontal = 16.dp)
                    )
                }
                item {
                    Spacer(modifier = Modifier.height(12.dp))
                }
                item {
                    LazyRow(
                        contentPadding = PaddingValues(horizontal = 16.dp),
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                        modifier = Modifier.height(250.dp)
                    ) {
                        items(uiState.favoriteEvents) { event ->
                            EventCard(
                                event = event,
                                isFavorite = true,
                                onEventClick = onEventClick,
                                onFavoriteClick = { viewModel.toggleFavorite(event.id) }
                            )
                        }
                    }
                }
                item {
                    Spacer(modifier = Modifier.height(32.dp))
                }
            }

            // Feed section (always shown when not searching)
            if (uiState.searchResults == null) {
                item {
                    EventFeedSectionHeader(
                        title = strings.recommendedForYou,
                        subtitle = strings.curatedEvents,
                        showFavorites = showFavorites,
                        onShowFeed = { showFavorites = false },
                        onShowFavorites = { showFavorites = true },
                        modifier = Modifier.padding(horizontal = 16.dp)
                    )
                }
                item {
                    Spacer(modifier = Modifier.height(12.dp))
                }

                val feedEvents = if (showFavorites) uiState.favoriteEvents else uiState.feedItems.map { it.event }
                if (feedEvents.isEmpty()) {
                    // Show empty state
                    item {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(32.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                text = if (showFavorites) strings.noResults else strings.noAvailableEvents,
                                style = MaterialTheme.typography.bodyLarge,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                } else {
                    // Display feed items as vertical list
                    items(feedEvents) { event ->
                        Column(
                            modifier = Modifier.padding(horizontal = 16.dp)
                        ) {
                            RecommendedEventCard(
                                event = event,
                                onEventClick = onEventClick,
                                onJoinClick = onEventClick
                            )
                            Spacer(modifier = Modifier.height(16.dp))
                        }
                    }

                    // Loading more indicator
                    if (uiState.isLoadingMore && !showFavorites) {
                        item {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(16.dp),
                                contentAlignment = Alignment.Center
                            ) {
                                CircularProgressIndicator()
                            }
                        }
                    }
                }

                item {
                    Spacer(modifier = Modifier.height(24.dp))
                }
            }
        }

        // Error message
        if (uiState.error != null) {
            item {
                Snackbar(
                    modifier = Modifier.padding(16.dp)
                ) {
                    Text(uiState.error!!)
                }
            }
        }
    }
}
